import React from "react";
import { Theme } from "../../../../providers/ThemeProvider";
import ScrollableWrapper from "../common/ScrollableWrapper";
import CustomLineChart from "./CustomLineChart";
import {
  PanelHeader,
  InfoTextBox,
  ExpandableInfoCard,
  SectionTitle,
  WeightCard,
  ScoreMeaningSection,
  TimelineGroup,
} from "./SidePanelShared";
import { usePanelData, PanelData } from "./usePanelData";
import { useScoreSidePanel } from "./ScoreSidePanelContext";
import UserBiomarkerPanel from "../healthOverview/UserBiomarkerPanel";

const InfoPanelContent = () => {
  const { contentType } = useScoreSidePanel();
  const { data, theme, closePanel } = usePanelData();

  // Health overview biomarker panel (About/Readings) renders its own layout —
  // it is backed by the biomarkers resource, not the report payload that
  // usePanelData reads.
  if (contentType === "userBiomarker") return <UserBiomarkerPanel />;

  if (!data) return null;

  return (
    <div style={styles.panelLayout}>
      <PanelHeader
        name={data.header.name}
        fullName={data.header.fullName}
        value={data.header.value}
        unit={data.header.unit}
        scoreCategory={data.header.scoreCategory}
        categoryColor={data.header.categoryColor}
        categoryLabel={data.header.categoryLabel}
        // Breath / blood biomarkers are scored on BIOMARKER_CATEGORY_LABELS,
        // systems and metrics on ScoreCategory — different pill palettes.
        categoryVariant={
          data.type === "breathBiomarker" || data.type === "bloodBiomarker"
            ? "biomarkers"
            : "score"
        }
        metricId={data.header.metricId}
        onClose={closePanel}
        theme={theme}
      />
      <ScrollableWrapper customPadding="0">
        <div style={styles.content}>{renderSections(data, theme)}</div>
      </ScrollableWrapper>
    </div>
  );
};

const renderSections = (data: PanelData, theme: Theme) => {
  switch (data.type) {
    case "system":
      return <SystemSections data={data} theme={theme} />;
    case "metric":
      return <MetricSections data={data} theme={theme} />;
    case "breathBiomarker":
      return <BreathBiomarkerSections data={data} theme={theme} />;
    case "bloodBiomarker":
      return <BloodBiomarkerSections data={data} theme={theme} />;
  }
};

// --- System Sections ---
const SystemSections = ({
  data,
  theme,
}: {
  data: Extract<PanelData, { type: "system" }>;
  theme: Theme;
}) => {
  const { system } = data;

  return (
    <>
      {system.description && (
        <div style={styles.descriptionPrimary(theme)}>{system.description}</div>
      )}

      {system.processedWeights?.some(
        (w) =>
          (w.variant === "other" &&
            w.biomarkerItems?.some((item) => item.weight !== 0)) ||
          (w.variant === "breath" &&
            w.metricGroups?.some(
              (g) => g.biomarkers.length > 0 && g.weight !== 0
            ))
      ) && (
        <div style={styles.section}>
          <div style={styles.compositionHeader}>
            <div style={styles.compositionTitle(theme)}>Score composition</div>
            <div style={styles.compositionSubtitle(theme)}>
              This score is composed of the following data categories
            </div>
          </div>
          <div style={styles.cardsColumn}>
            {system.processedWeights.map((weight, index) => (
              <WeightCard
                key={`${data.header.name}-${index}`}
                weight={weight}
                theme={theme}
              />
            ))}
          </div>
        </div>
      )}

      <ScoreMeaningSection items={system.whatDoesYourScoreMean} theme={theme} />
    </>
  );
};

// --- Metric Sections ---
const MetricSections = ({
  data,
  theme,
}: {
  data: Extract<PanelData, { type: "metric" }>;
  theme: Theme;
}) => {
  const { metric } = data;
  return (
    <>
      {metric.description ? (
        <div style={styles.descriptionPrimary(theme)}>{metric.description}</div>
      ) : (
        <div style={styles.descriptionPrimary(theme)}>No description</div>
      )}

      {metric.whyItIsImportantToTrackText && (
        <InfoTextBox
          title="Why it is important to track"
          text={metric.whyItIsImportantToTrackText}
          theme={theme}
        />
      )}

      {metric.howItsMeasuredText && (
        <InfoTextBox
          title="What it shows"
          text={metric.howItsMeasuredText}
          theme={theme}
        />
      )}

      <TimelineGroup
        theme={theme}
        influencedBy={metric.influencedBy}
        score={metric.score}
        scoreUnit="%"
        scoreCategory={metric.scoreCategory}
        categoryColor={metric.categoryColor}
        metricId={metric.id}
        affectsItems={{ positive: [], negative: metric.lowerScoreImpacts }}
      />

      {metric.habitsToLetGo.length > 0 && (
        <div style={styles.section}>
          <SectionTitle
            title={metric.habitsToLetGoTitle || "What to avoid"}
            theme={theme}
          />
          <div style={styles.cardsColumn}>
            {metric.habitsToLetGo.map((h: any, idx: number) => (
              <ExpandableInfoCard
                key={idx}
                title={h.label}
                description={h.description}
                theme={theme}
              />
            ))}
          </div>
        </div>
      )}

      {metric.chartData.length > 0 && (
        <div style={styles.section}>
          <SectionTitle title="Chart" theme={theme} />
          <CustomLineChart
            data={metric.chartData}
            id={metric.id}
            unit={metric.unit}
          />
        </div>
      )}
    </>
  );
};

// --- Breath Biomarker Sections ---
const BreathBiomarkerSections = ({
  data,
  theme,
}: {
  data: Extract<PanelData, { type: "breathBiomarker" }>;
  theme: Theme;
}) => {
  const { breathBiomarker } = data;
  return (
    <>
      <div style={styles.descriptionPrimary(theme)}>
        {breathBiomarker.description}
      </div>

      {breathBiomarker.chartData.length > 0 && (
        <div style={styles.section}>
          <SectionTitle title="Chart" theme={theme} />
          <CustomLineChart
            data={breathBiomarker.chartData}
            id={breathBiomarker.id}
            unit={breathBiomarker.unit}
          />
        </div>
      )}
    </>
  );
};

// --- Blood Biomarker Sections ---
const BloodBiomarkerSections = ({
  data,
  theme,
}: {
  data: Extract<PanelData, { type: "bloodBiomarker" }>;
  theme: Theme;
}) => {
  const { bloodBiomarker } = data;
  return (
    <>
      <div
        style={styles.descriptionPrimary(theme)}
        dangerouslySetInnerHTML={{ __html: bloodBiomarker.description }}
      />

      <TimelineGroup
        theme={theme}
        influencedBy={bloodBiomarker.influencedBy}
        score={data.header.value}
        scoreUnit={` ${data.header.unit}`}
        scoreCategory={bloodBiomarker.scoreCategory}
        categoryColor={bloodBiomarker.categoryColor}
        categoryLabel={data.header.categoryLabel}
        categoryVariant="biomarkers"
        affectsItems={{ positive: [], negative: bloodBiomarker.affectsItems }}
      />

      {bloodBiomarker.chartData.length > 0 && (
        <div style={styles.section}>
          <SectionTitle title="Chart" theme={theme} />
          <CustomLineChart
            data={bloodBiomarker.chartData}
            id={bloodBiomarker.id}
            unit={bloodBiomarker.unit}
          />
        </div>
      )}
    </>
  );
};

export default InfoPanelContent;

const styles = {
  panelLayout: {
    display: "flex",
    flexDirection: "column" as const,
    flex: 1,
    minHeight: 0,
    gap: "24px",
  },
  content: {
    display: "flex",
    flexDirection: "column" as const,
    gap: "24px",
  },
  descriptionPrimary: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    lineHeight: "1.4",
    letterSpacing: "-0.24px",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
  }),
  section: {
    display: "flex",
    flexDirection: "column" as const,
    gap: "12px",
  },
  compositionHeader: {
    display: "flex",
    flexDirection: "column" as const,
    gap: "4px",
  },
  compositionTitle: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "14px",
    lineHeight: "normal",
    letterSpacing: "-0.13px",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
  }),
  compositionSubtitle: (theme: Theme) => ({
    fontFamily: "var(--font-Regular)",
    fontSize: "12px",
    lineHeight: "normal",
    letterSpacing: "-0.13px",
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
  }),
  cardsColumn: {
    display: "flex",
    flexDirection: "column" as const,
    gap: "8px",
  },
};
